import * as z from 'zod'
import { Option } from '~/shared/config/constants'
import {
  dateSchema,
  dateTimeSchema,
  detailsSchema,
  enumOptionSchema,
  enumOptionLabelSchema,
  phonesArraySchema,
  uuidOptionsSchema,
  nullableNumberSchema,
} from '~/shared/lib/schemas'
import { isString } from '~/shared/lib/utils'
import {
  CategoryAutoEnum,
  FuelTypeEnum,
  TransmissionEnum,
} from '../config/enums'
import { CarBrand } from './carBrand'
import { CarModel } from './carModel'
import { ApiModel, ToManyRelation } from './core'
import { Region } from './regions'

const attributeSchema = z.object({
  code: z.string().optional().nullable(),
  title: z.string().trim().min(1, 'Обязательное поле'),
  description: z.string().optional().nullable(),
  usagesLeft: nullableNumberSchema,
  boundToUser: z.boolean().optional(),
  multiUse: z.boolean().optional(),
  details: detailsSchema,
  expiredAt: dateTimeSchema.optional().nullable(),
  isPromotion: z.boolean(),
  isActive: z.boolean(),
  beginningAt: dateTimeSchema.optional().nullable(),
  usageLimitPerUser: nullableNumberSchema,
  driverPhones: phonesArraySchema.optional().nullable(),
  isFirstContract: z.boolean(),
  carYearsOfManufacture: z.array(z.number()).optional().nullable(),
  carTypesTransmission: z.array(enumOptionSchema(TransmissionEnum)).optional(),
  carTypesFuel: z.array(enumOptionSchema(FuelTypeEnum)).optional(),
  carCategories: z.array(enumOptionLabelSchema(CategoryAutoEnum)).optional(),
  carVinList: z.array(z.string()).optional().nullable(),
})

const relationsSchema = z.object({
  regionsIds: uuidOptionsSchema.optional(),
  carBrandsIds: uuidOptionsSchema.optional(),
  carModelsIds: uuidOptionsSchema.optional(),
  carRegionsIds: uuidOptionsSchema.optional(),
})

const schema = z
  .object({
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
  })
  .merge(attributeSchema)
  .merge(relationsSchema)

export type PromocodeAttributes = z.infer<typeof schema>

export class Promocode extends ApiModel<typeof schema> {
  static jsonApiType = 'promocodes'
  static schema = schema

  isPromotion(): boolean {
    return this.getAttribute('isPromotion')
  }

  getTitle(): string {
    return this.getAttribute('title')
  }

  driverRegions(): ToManyRelation<Region, this> {
    return this.hasMany(Region)
  }

  carRegions(): ToManyRelation<Region, this> {
    return this.hasMany(Region, 'carRegions')
  }

  carBrands(): ToManyRelation<CarBrand, this> {
    return this.hasMany(CarBrand)
  }

  carModels(): ToManyRelation<CarModel, this> {
    return this.hasMany(CarModel)
  }

  getRegions(): Region[] {
    return this.getRelation('driverRegions')
  }

  getCarModels(): CarModel[] {
    return this.getRelation('carModels')
  }

  getCarBrands(): CarBrand[] {
    return this.getRelation('carBrands')
  }

  getCarRegions(): Region[] {
    return this.getRelation('carRegions')
  }

  setRegions(ids: UniqueId[] | Option[]) {
    let regions: Region[] = []
    ids.forEach((regionId) => {
      const region = new Region()
      const id = isString(regionId) ? regionId : regionId.id
      if (isString(id)) {
        region.setApiId(id)
        regions = regions.concat(region)
      }
    })
    this.setRelation('driverRegions', regions)
  }

  setCarRegions(ids: UniqueId[] | Option[]) {
    let carRegions: Region[] = []
    ids.forEach((regionId) => {
      const region = new Region()
      const id = isString(regionId) ? regionId : regionId.id
      if (isString(id)) {
        region.setApiId(id)
        carRegions = carRegions.concat(region)
      }
    })
    this.setRelation('carRegions', carRegions)
  }

  setCarBrands(ids: UniqueId[] | Option[]) {
    let carBrands: CarBrand[] = []
    ids.forEach((carBrandId) => {
      const carBrand = new CarBrand()
      const id = isString(carBrandId) ? carBrandId : carBrandId.id
      if (isString(id)) {
        carBrand.setApiId(id)
        carBrands = carBrands.concat(carBrand)
      }
    })
    this.setRelation('carBrands', carBrands)
  }

  setCarModels(ids: UniqueId[] | Option[]) {
    let carModels: CarModel[] = []
    ids.forEach((carModelId) => {
      const carModel = new CarModel()
      const id = isString(carModelId) ? carModelId : carModelId.id
      if (isString(id)) {
        carModel.setApiId(id)
        carModels = carModels.concat(carModel)
      }
    })
    this.setRelation('carModels', carModels)
  }

  // Custom save method to include empty relationships arrays
  // This fixes the issue where library doesn't include empty arrays in the payload
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async save(): Promise<any> {
    // If no ID, this is a new record - use parent save method
    if (!this.getApiId()) {
      return super.save()
    }

    // For updates, include all relationships (even empty arrays)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = (this as any).serialize()

    if (!payload.data.relationships) {
      payload.data.relationships = {}
    }

    const relationKeys = [
      'driverRegions',
      'carRegions',
      'carBrands',
      'carModels',
    ]
    relationKeys.forEach((key) => {
      const relation = this.getRelation(key)
      if (Array.isArray(relation)) {
        payload.data.relationships[key] = {
          data: relation.map((model) => ({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            type: (model.constructor as any).jsonApiType,
            id: model.getApiId(),
          })),
        }
      }
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const httpClient = (this.constructor as any).httpClient
    const url =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.constructor as any).getJsonApiUrl() + '/' + this.getApiId()

    return await httpClient.patch(url, payload)
  }
}
